import React, { useEffect, useState } from 'react';
import { PERMISSIONS } from '../../constants';
import { maxValue, minValue, NumberInput, NumberInputProps, useGetOne, usePermissions } from 'react-admin';
import { requiredAllowZero } from '../../utils/utils';

type TaxLevel = '1' | '2' | '3';
type TaxType = 'default' | 'special';

export interface ChainTaxPermissionLevelInputProps extends NumberInputProps {
  type: TaxType,
}

const ChainTaxPermissionLevelInput: React.FC<ChainTaxPermissionLevelInputProps> = (props) => {

  const {
    label,
    source,
    type = 'default',
    ...restProps
  } = props;

  const { permissions } = usePermissions();
  const { data, isLoading } = useGetOne('system-config', { id: 1 });
  const [minPercentage, setMinPercentage] = useState(0);

  useEffect(() => {
    if (!isLoading && data) {
      let taxDiscount = 0;

      if (permissions?.length && permissions.includes(PERMISSIONS.EDIT_CHAIN_SYSTEM_DEFAULT_TAX)) {
        taxDiscount = 100
      } else if (checkHasPermission('3', type)) {
        taxDiscount = data.chainLevel3MaxTaxDiscount;
      } else if (checkHasPermission('2', type)) {
        taxDiscount = data.chainLevel2MaxTaxDiscount;
      } else if (checkHasPermission('1', type)) {
        taxDiscount = data.chainLevel1MaxTaxDiscount;
      }

      const minTax = Math.round(data.chainDefaultTax * (1 - (taxDiscount / 100)) * 100) / 100;
      setMinPercentage(minTax);
    }
  }, [data, isLoading]);

  const hasEditChainTaxPermission = permissions?.length && permissions.some((permission: string) =>
    [PERMISSIONS.EDIT_CHAIN_TAX_LEVEL_1, PERMISSIONS.EDIT_CHAIN_TAX_LEVEL_2, PERMISSIONS.EDIT_CHAIN_TAX_LEVEL_3].includes(permission)
  );

  const hasEditChainSpecialTaxPermission = permissions?.length && permissions.some((permission: string) =>
    [PERMISSIONS.EDIT_CHAIN_SPECIAL_TAX_LEVEL_1, PERMISSIONS.EDIT_CHAIN_SPECIAL_TAX_LEVEL_2, PERMISSIONS.EDIT_CHAIN_SPECIAL_TAX_LEVEL_3].includes(permission)
  );

  const isRequired = type === 'special' ? false : permissions?.length && !permissions.includes(PERMISSIONS.EDIT_CHAIN_SYSTEM_DEFAULT_TAX);

  const checkHasPermission = (level: TaxLevel, type: TaxType) => {

    const taxPermissions = {
      default: {
        '1': PERMISSIONS.EDIT_CHAIN_TAX_LEVEL_1,
        '2': PERMISSIONS.EDIT_CHAIN_TAX_LEVEL_2,
        '3': PERMISSIONS.EDIT_CHAIN_TAX_LEVEL_3,
      },
      special: {
        '1': PERMISSIONS.EDIT_CHAIN_SPECIAL_TAX_LEVEL_1,
        '2': PERMISSIONS.EDIT_CHAIN_SPECIAL_TAX_LEVEL_2,
        '3': PERMISSIONS.EDIT_CHAIN_SPECIAL_TAX_LEVEL_3,
      }
    }

    const permissionKey = taxPermissions[type]?.[level];

    return permissions?.length && permissionKey && permissions.includes(permissionKey);
  }

  return (
    <>
      <NumberInput
        {...restProps}
        label={label}
        source={source}
        validate={[minValue(minPercentage), maxValue(100), ...(isRequired ? [requiredAllowZero()] : [])]}
        disabled={isLoading || (type === 'default' ? !hasEditChainTaxPermission : type === 'special' ? !hasEditChainSpecialTaxPermission : true)}
        isRequired={isRequired}
      />
    </>
  );
}

export default ChainTaxPermissionLevelInput;